import { X } from '@phosphor-icons/react';
import { Button } from '../../../Utility/Button';
import { Prompt, Model, PromptFunction } from '../../../../Types';
import Checkbox from '../../../Utility/FormElements/Checkbox';
import React from 'react';
import { AssistantTabs, ChatMessageRole, DocumentProcessingType } from '../../../../Types/Enums';
import Avatar from '../../../Utility/Avatar';
import TabSection from './TabSection';
import { Badge } from '../../../Utility/Badge';
import TabHeadline from './TabHeadline';
import InfoButton from '../../../Utility/InfoButton';

interface FunctionsTabProps {
    prompt: Prompt;
    models: Model[];
    userPrompt: boolean;
    promptFunctions: PromptFunction[];
    i18n: any;
    handlePromptFunctionCheckboxChange: any;
    isAssistantModel: boolean;
    PromptActions: any;
    lastClickedElementRef: any;
    filesInputKey: any;
    setIsAssistantModel: any;
    mimeTypes: any[];
    settings: any;
}

function FunctionsTab({
    prompt,
    models,
    userPrompt,
    i18n,
    handlePromptFunctionCheckboxChange,
    PromptActions,
    isAssistantModel,
    lastClickedElementRef,
    filesInputKey,
    setIsAssistantModel,
    mimeTypes,
    settings,
}: FunctionsTabProps) {
    const modalContent = (
        <React.Fragment>
            <h3 className="m-0 p-0 mb-4">
                {i18n.translate({
                    id: 'modelFunctionSwitch.title',
                    sourceName: 'Prompt',
                })}
            </h3>
            <p className="text-layout-textcolor">
                {i18n.translate({
                    id: 'modelFunctionSwitch.description',
                    sourceName: 'Prompt',
                })}
            </p>
            <div className="max-h-[calc(var(--app-height)/2)] h-full overflow-y-auto mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 mt-5">
                {models
                    .filter((model) => model.isAssistantModel)
                    .map((model) => (
                        <React.Fragment key={model.identifier}>
                            <div
                                key={model.identifier}
                                className={`relative border-[2px] border-layout-borderColor overflow-hidden rounded-xl cursor-pointer text-center hover:border-primary transition-all duration-150`}
                                onClick={() => {
                                    PromptActions.handleDropdownChange(model.identifier);
                                    setIsAssistantModel(model.isAssistantModel);
                                    PromptActions.closeFileClickModal();
                                }}
                            >
                                <div className="bg-white h-1/2 p-2 flex items-center justify-center">
                                    <div className="w-10 h-10 relative">
                                        <Avatar
                                            role={ChatMessageRole.assistant}
                                            size={40}
                                            avatarImage={model.image}
                                            title={model.title}
                                        />
                                    </div>
                                </div>
                                <div className="h-1/2  p-4 flex grow justify-center items-center">
                                    <p className="p-0 h-[48px] m-0 text-layout-textcolor text-wrap break-words w-full">
                                        {model.title}
                                    </p>
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
            </div>
        </React.Fragment>
    );

    return (
        <div>
            <TabHeadline
                title={i18n.translate({
                    id: 'tabs.functions.title',
                    sourceName: 'Prompt',
                })}
            />
            <InfoButton configKey="functions" />

            <div>
                <TabSection
                    title={i18n.translate({
                        id: 'tabs.functions.section.functions.title',
                        sourceName: 'Prompt',
                    })}
                    subline={i18n.translate({
                        id: 'tabs.functions.section.functions.subtitle',
                        sourceName: 'Prompt',
                    })}
                >
                    <div className="flex flex-col gap-1 my-3">
                        <Checkbox
                            key="code_interpreter"
                            checked={prompt.functions.length > 0 && prompt.functions.includes('code_interpreter')}
                            name="code_interpreter"
                            onChange={(e) => {
                                if (!isAssistantModel) {
                                    e.preventDefault();
                                    PromptActions.handleFileClickModal(modalContent);
                                    lastClickedElementRef.current = e.currentTarget;
                                } else {
                                    PromptActions.handlePromptFunctionCheckboxChange(e);
                                }
                            }}
                            label={i18n.translate({
                                id: 'codeInterpreter',
                                sourceName: 'Prompt',
                            })}
                        />
                        <Checkbox
                            key="local_document_processing"
                            checked={prompt.documentProcessing === DocumentProcessingType.local}
                            name="local_document_processing"
                            onChange={(e) => {
                                PromptActions.handleLocalDocumentProcessingChange(e);
                            }}
                            label={i18n.translate({
                                id: 'localDocumentProcessing',
                                sourceName: 'Prompt',
                            })}
                        />
                        {settings.openaiDocumentProcessingEnabled && (
                            <Checkbox
                                key="openai_document_processing"
                                checked={prompt.documentProcessing === DocumentProcessingType.openai}
                                name="openai_document_processing"
                                onChange={(e) => {
                                    if (!isAssistantModel) {
                                        e.preventDefault();
                                        PromptActions.handleFileClickModal(modalContent);
                                        lastClickedElementRef.current = e.currentTarget;
                                    } else {
                                        PromptActions.handleOpenAIDocumentProcessingChange(e);
                                    }
                                }}
                                label={i18n.translate({
                                    id: 'openaiDocumentProcessing',
                                    sourceName: 'Prompt',
                                })}
                            />
                        )}
                    </div>
                </TabSection>

                <TabSection
                    title={i18n.translate({ id: 'tabs.functions.section.files.title', sourceName: 'Prompt' })}
                    subline={i18n.translate({ id: 'tabs.functions.section.files.subtitle', sourceName: 'Prompt' })}
                >
                    <input
                        type="file"
                        id="filesInput"
                        className="hidden"
                        multiple
                        onChange={PromptActions.handleFileChange}
                        key={filesInputKey}
                        accept={mimeTypes.join(',')}
                        title={i18n.translate({ id: 'filesUpload.button', sourceName: 'Prompt' })}
                    />
                    <Badge variant="info" className="mb-4">
                        <div>
                            <span className="block font-bold mb-1">
                                {i18n.translate({
                                    id: 'tabs.functions.section.files.infoText.title',
                                    sourceName: 'Prompt',
                                })}
                            </span>
                            <span>
                                {i18n.translate({
                                    id: 'tabs.functions.section.files.infoText.content',
                                    sourceName: 'Prompt',
                                })}
                            </span>
                        </div>
                    </Badge>

                    {prompt.files?.length > 0 && (
                        <ul className="mt-0">
                            {prompt.files?.map((file: any, index: any) => (
                                <li className="my-1" key={index} onClick={() => PromptActions.removeFile(index)}>
                                    {file.name}
                                    <span className="ml-1 hover:cursor-pointer">
                                        <X className="inline text-primary " />
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <Button
                        variant="secondary"
                        className="w-full max-w-[200px] mb-4"
                        onClick={(e) => {
                            if (prompt.documentProcessing === DocumentProcessingType.local || isAssistantModel) {
                                PromptActions.handleFilesButtonClick();
                            } else {
                                PromptActions.handleFileClickModal(modalContent);
                                lastClickedElementRef.current = e.currentTarget;
                            }
                        }}
                    >
                        {i18n.translate({ id: 'filesUpload.button', sourceName: 'Prompt' })}
                    </Button>

                    <p>
                        {i18n.translate({
                            id: 'tabs.functions.section.files.infoText.mimeType',
                            sourceName: 'Prompt',
                        })}{' '}
                        <span
                            onClick={() => PromptActions.setSelectedTab(AssistantTabs.model)}
                            className="underline cursor-pointer hover:text-primary transition-colors duration-100"
                        >
                            {i18n.translate({
                                id: 'tabs.functions.section.files.infoText.mimeType.link',
                                sourceName: 'Prompt',
                            })}
                        </span>
                    </p>
                </TabSection>
            </div>
        </div>
    );
}

export default FunctionsTab;
